import { useCallback, useMemo, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Empty, Input, Menu } from "antd5";
import { escapeRegExp } from "lodash";
import { Text } from "styles/utility-components";

import { DetailsContent, DetailsHeader } from "components/app_layout/DetailsLayout";
import UserInitials from "components/comments/UserInitials";
import { AddNewListButton } from "components/user_lists/AddNewListButton";
import UserName, { formatUserNameWithStatus } from "components/users/UserName";
import { numberSort, stringSort } from "lib/columnSort";
import { EllipsisTooltipRouterLink } from "lib/core_components/EllipsisTooltip";
import SkeletonTable from "lib/core_components/SkeletonTable";
import { Table } from "lib/core_components/Table";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import { NoticeListSummary, useNoticeLists } from "lib/hooks/api/notices/lists/useNoticeLists";
import { UserSearchResult } from "lib/hooks/api/users/useUsersGraphql";
import { useUsers } from "lib/hooks/api/useUsers";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { EditListMenu } from "./EditListMenu";
import { noticeSearchTarget } from "./utils";

import css from "./NoticeLists.module.scss";

function buildColumns(
  companyUsers: UserSearchResult[] | undefined,
): ColumnType<NoticeListSummary>[] {
  // Resolve activity against the company's users (including inactive ones) so the owner cell can
  // show the greyed "(deactivated)" suffix; owners no longer in the company show as active.
  const resolveUser = (owner: NoticeListSummary["owner"]) => {
    const matched = companyUsers?.find((u) => u.guid === owner.guid);
    return matched ?? { ...owner, active: true };
  };

  return [
    {
      title: "List name",
      dataIndex: "name",
      key: "name",
      sizeConfig: { small: 200, medium: 250, large: 400, xlarge: 450 },
      ellipsis: true,
      render: (_, list) => (
        <div className={css.nameCell}>
          <EllipsisTooltipRouterLink
            fullText={list.name}
            linkText={<b>{list.name}</b>}
            linkProps={{
              ...noticeSearchTarget({ noticeLists: [list.id.toString()] }),
            }}
          />
          <EditListMenu list={list} />
        </div>
      ),
      sorter: stringSort((list) => list.name),
      sortDirections: ["ascend", "descend", "ascend"],
    },
    {
      title: "Notices",
      dataIndex: "recordCount",
      key: "record_count",
      sizeConfig: { small: 80, medium: 80, large: 120, xlarge: 160 },
      render: (_, { recordCount }) => recordCount,
      sorter: numberSort((list) => list.recordCount),
      defaultSortOrder: "descend",
      sortDirections: ["descend", "ascend", "descend"],
    },
    // TODO: add once we have this functionality
    // {
    //   title: "Shared with",
    //   dataIndex: "visibility",
    //   key: "visibility",
    //   sizeConfig: { small: 200, medium: 250, large: 400, xlarge: 450 },
    // },
    {
      title: "Owner",
      key: "owner_name",
      render: (_, { owner }) => {
        const resolved = resolveUser(owner);
        return (
          <div className={css.ownerCell}>
            <UserInitials firstName={resolved.firstName} lastName={resolved.lastName} />
            <UserName user={resolved} />
          </div>
        );
      },
      sorter: stringSort((list) => formatUserNameWithStatus(resolveUser(list.owner))),
    },
  ];
}

// Copy of BuyerLists.tsx and modified to fit the data structure needed
function NoticeLists() {
  const { data: lists, isLoading, isFetching, isError } = useNoticeLists();
  // includeInactive so an owner who has since been deactivated still resolves in the owner column.
  const { data: companyUsers } = useUsers({ includeInactive: true });
  const columns = useMemo(() => buildColumns(companyUsers), [companyUsers]);
  const [searchValue, setSearchValue] = useState("");
  const { logEvent } = useTracking();

  const [tab, setTab] = useState<"allNoticeLists" | "myNoticeLists">("allNoticeLists");

  const onTabChange = useCallback(
    (key: "allNoticeLists" | "myNoticeLists") => {
      logEvent(EventNames.tabChanged, {
        "Tab selected": key === "allNoticeLists" ? "All notice lists" : "My notice lists",
      });
      setTab(key);
    },
    [logEvent],
  );

  const myLists = useMemo(() => lists?.filter((list) => list.isOwner), [lists]);

  const displayedLists: NoticeListSummary[] = useMemo(() => {
    const seletedlists = tab === "allNoticeLists" ? lists : myLists;
    return (
      seletedlists?.filter((list) => list.name.match(new RegExp(escapeRegExp(searchValue), "i"))) ||
      []
    );
  }, [lists, myLists, searchValue, tab]);

  /**
   * Want to display the search box and menu tabs when loading or when data exists. Just so there's as little as possible
   * disruption when user is loading the screen most of the time. We only obfuscate these CTAs (if that's the right word for it)
   * when there is no data
   */
  const dataExists = useMemo(
    (): boolean => (lists && lists.length > 0) || isLoading,
    [isLoading, lists],
  );

  const displayedTable = useMemo(() => {
    if (isLoading) {
      return <SkeletonTable columns={columns} pageSize={10} active />;
    }

    return (
      <TrackingProvider data={{ "Context source": "In-row" }}>
        <Table
          columns={columns}
          loading={isFetching}
          isError={isError}
          dataSource={displayedLists}
          rowKey={(r) => r.id}
          locale={{
            emptyText: (
              <Empty className={css.emptyState}>
                <h2>No notice lists created yet</h2>
              </Empty>
            ),
          }}
          pagination={displayedLists.length < 20 ? false : undefined}
          onRow={(record, _) => {
            return {
              onClick: () => {
                logEvent(EventNames.noticeListClicked, {
                  "List type": "Notice list",
                  "List name": record.name,
                  "List ID": record.id,
                });
              },
            };
          }}
        />
      </TrackingProvider>
    );
  }, [columns, displayedLists, isError, isFetching, isLoading, logEvent]);

  return (
    <>
      <DetailsHeader className={css.noticeListHeader}>
        <div className={css.titleAndBtn}>
          <Text h1>Notice lists</Text>
          <AddNewListButton modal />
        </div>
        {dataExists ? (
          <Menu
            mode="horizontal"
            selectedKeys={[tab]}
            className={css.menuTabs}
            items={[
              {
                key: "allNoticeLists",
                onClick: () => onTabChange("allNoticeLists"),
                className: css.menuTabItem,
                label: (
                  <span key="allNoticeLists">
                    All notice lists {lists?.length !== undefined && `(${lists.length})`}
                  </span>
                ),
              },
              {
                key: "myNoticeLists",
                onClick: () => onTabChange("myNoticeLists"),
                className: css.menuTabItem,
                label: (
                  <span key="myNoticeLists">
                    My notice lists {myLists?.length !== undefined && `(${myLists.length})`}
                  </span>
                ),
              },
            ]}
          />
        ) : (
          // Just having this padding for no data to make header look a bit cleaner when tabs above aren't displayed
          <span style={{ paddingBottom: "16px" }} />
        )}
      </DetailsHeader>

      <div className={css.searchContainer}>
        {dataExists && (
          <Input
            placeholder="Search lists"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className={css.searchInput}
            prefix={<SearchOutlined />}
            allowClear
          />
        )}
      </div>
      <DetailsContent>{displayedTable}</DetailsContent>
    </>
  );
}

export default NoticeLists;
